import {useEffect, useState, useRef, useCallback} from 'react'
import {useNavigate} from 'react-router-dom'
import axios from 'axios'
import {Button} from "@origamirisk/origami-storybook"
import {Box, TextField} from '@mui/material'
import {useAccount} from '../context/AccountContext'
import {ModuleRegistry, AllCommunityModule} from 'ag-grid-community'
import {
    VersionsTable,
    VersionDetailsModal,
    Version,
    VersionsTableRef
} from '../components/Versions'
import {ImportJobsPanel, JobsButton, ImportJob} from '../components/ImportJobs'
import {ResetVersionDataDialog} from '../components/ResetVersionDataDialog'
import {useImportJobsSSE} from '../hooks/useImportJobsSSE'
import './ReleasesAndVersions.css'

ModuleRegistry.registerModules([AllCommunityModule])

function ReleasesAndVersions() {
    const navigate = useNavigate()
    const {selectedAccount, selectedAccountConnection, isAdminUser} = useAccount()
    const [versions, setVersions] = useState<Version[]>([])
    const [loadingVersions, setLoadingVersions] = useState(false)
    const [searchText, setSearchText] = useState<string>('')
    const [selectedVersion, setSelectedVersion] = useState<Version | null>(null)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isImportPanelOpen, setIsImportPanelOpen] = useState(false)
    const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
    const [importJobs, setImportJobs] = useState<ImportJob[]>([])
    const [showDeleted, setShowDeleted] = useState(false)
    const tableRef = useRef<VersionsTableRef>(null)

    const handleSearchChange = (value: string) => {
        setSearchText(value)
        tableRef.current?.onFilterChanged()
    }

    // Get account connection ID from context
    const accountConnectionId = selectedAccountConnection?.id || null

    // Initial load of jobs
    useEffect(() => {
        const fetchJobs = async () => {
            if (!selectedAccount) {
                setImportJobs([])
                return
            }

            try {
                const response = await fetch('/api/import-jobs/list', {
                    method: 'POST',
                    headers: {'Content-Type': 'application/json'},
                    body: JSON.stringify({accountName: selectedAccount, limit: 50})
                })

                if (response.ok) {
                    const data = await response.json()
                    setImportJobs(data)
                }
            } catch (error) {
                console.error('Failed to fetch jobs:', error)
            }
        }

        fetchJobs()
    }, [selectedAccount])

    // Handle real-time job updates via SSE
    const handleJobUpdate = useCallback((updatedJob: ImportJob) => {
        setImportJobs(prevJobs => {
            // Remove cancelled or dead-lettered jobs from the list
            if (updatedJob.status === 'CANCELLED' || updatedJob.status === 'DEAD_LETTERED') {
                return prevJobs.filter(j => j.jobId !== updatedJob.jobId)
            }

            const existingIndex = prevJobs.findIndex(j => j.jobId === updatedJob.jobId)

            if (existingIndex >= 0) {
                // Update existing job
                const newJobs = [...prevJobs]
                newJobs[existingIndex] = updatedJob
                return newJobs
            } else {
                // Add new job at the beginning
                return [updatedJob, ...prevJobs]
            }
        })
    }, [])

    const handleSSEError = useCallback((error: Error) => {
        console.error('SSE error:', error)
    }, [])

    // Use SSE for real-time updates
    useImportJobsSSE({
        accountConnectionId,
        enabled: true, // Always enabled in this view
        onJobUpdate: handleJobUpdate,
        onError: handleSSEError
    })

    useEffect(() => {
        if (!selectedAccountConnection) {
            setVersions([])
            return
        }

        const controller = new AbortController()

        const fetchVersions = async () => {
            setLoadingVersions(true)
            try {
                const {data} = await axios.post<Version[]>('/api/admin/versions', {
                    includeDeleted: showDeleted
                }, {signal: controller.signal})
                setVersions(data)
            } catch (e) {
                if (axios.isAxiosError(e) && e.code === 'ERR_CANCELED') {
                    return
                }
                console.error('Failed to fetch versions:', e)
                setVersions([])
            } finally {
                if (!controller.signal.aborted) {
                    setLoadingVersions(false)
                }
            }
        }

        fetchVersions()
        return () => controller.abort()
    }, [selectedAccountConnection?.id, showDeleted])

    const handleRowClick = (version: Version) => {
        setSelectedVersion(version)
        setIsModalOpen(true)
    }

    const handleOpenImportPanel = () => {
        setIsImportPanelOpen(true)
    }

    const handleVersionUpdated = async () => {
        // Refresh versions list after delete/undelete
        if (!selectedAccount) return

        setLoadingVersions(true)
        try {
            const {data} = await axios.post<Version[]>('/api/admin/versions', {
                includeDeleted: showDeleted
            })
            setVersions(data)
            setIsModalOpen(false)
        } catch (e) {
            console.error('Failed to refresh versions:', e)
        } finally {
            setLoadingVersions(false)
        }
    }

    const handleResetComplete = () => {
        // Refresh versions list after reset
        handleVersionUpdated()
    }

    // Calculate active jobs count
    const activeJobsCount = importJobs.filter(
        job => job.status === 'QUEUED' || job.status === 'IN_PROGRESS'
    ).length

    return (
        <div className="releases-and-versions" style={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxSizing: 'border-box'
        }}>
            {selectedAccount ? (
                <div className="versions-wrapper"
                     style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0}}>
                    <div className="versions-title-wrapper" style={{flexShrink: 0}}>
                        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                            <TextField
                                placeholder="Search versions..."
                                size="small"
                                style={{width: '300px'}}
                                value={searchText}
                                onChange={(e) => handleSearchChange(e.target.value)}
                            />
                            <Button
                                variant="contained"
                                color={showDeleted ? 'primary' : 'secondary'}
                                size="md"
                                onClick={() => setShowDeleted(!showDeleted)}
                            >
                                {showDeleted ? 'Hide deleted' : 'Show deleted'}
                            </Button>
                        </Box>

                        <Box sx={{display: 'flex', gap: 2, alignItems: 'center'}}>
                            <Button
                                color="secondary"
                                variant="contained"
                                size="md"
                                onClick={() => navigate('/version-publisher', {state: {selectedAccount}})}
                                disabled={!selectedAccount}
                            >
                                Publish versions...
                            </Button>
                            {isAdminUser && (
                                <Button
                                    color="error"
                                    variant="outlined"
                                    size="md"
                                    onClick={() => setIsResetDialogOpen(true)}
                                    disabled={!selectedAccount}
                                >
                                    Reset Version Data
                                </Button>
                            )}
                            <JobsButton
                                activeJobsCount={activeJobsCount}
                                onClick={handleOpenImportPanel}
                                disabled={!selectedAccount}
                            />
                        </Box>
                    </div>
                    <div style={{flex: 1, minHeight: 0, overflow: 'hidden'}}>
                        <VersionsTable
                            ref={tableRef}
                            versions={versions}
                            loading={loadingVersions}
                            searchText={searchText}
                            showDeleted={showDeleted}
                            onRowClick={handleRowClick}
                        />
                    </div>
                </div>
            ) : (
                <div style={{padding: '20px', color: '#666'}}>
                    Please select an account from the sidebar to view versions.
                </div>
            )}

            {isModalOpen && selectedVersion && (
                <VersionDetailsModal
                    isOpen={isModalOpen}
                    version={selectedVersion}
                    onClose={() => setIsModalOpen(false)}
                    onVersionUpdated={handleVersionUpdated}
                />
            )}

            <ImportJobsPanel
                open={isImportPanelOpen}
                onClose={() => setIsImportPanelOpen(false)}
            />

            <ResetVersionDataDialog
                isOpen={isResetDialogOpen}
                onClose={() => setIsResetDialogOpen(false)}
                onResetComplete={handleResetComplete}
            />
        </div>
    )
}

export default ReleasesAndVersions
